<template>
    <div class="u-position-relative">
        <!-- optional indicators -->
        <div class="a-loader--typeahead" v-if="loading"></div>

        <control for="typeaheadField" :label="label">
            <input type="text"
                   class="input"
                   name="typeaheadField"
                   maxlength="100"
                   v-field-control:typeaheadField.required="{type:'text', rules:'name'}"
                   required
                   autocomplete="off"
                   v-model="query"
                   @keydown.down="down"
                   @keydown.up="up"
                   @keydown.enter="hit"
                   @keydown.esc="reset"
                   @blur="reset"
                   @input="update"/>
            <control-error :show="$error('typeaheadField')">
                {{ errorMessage }}
            </control-error>
        </control>

        <ul v-show="hasItems && inFocus">
            <li v-for="(item, $item) in items" :class="activeClass($item)" @mousedown="hit"
                @mousemove="setActive($item)">
                <span class="name" v-text="item.name"></span>
                <span class="screen-name" v-text="item.screen_name"></span>
            </li>
        </ul>

    </div>
</template>

<script>
    import Control from 'components/form/Control.vue';
    import ControlError from 'components/form/control/Error.vue';
    import FormValidator from 'components/form/validator/mixin';
    import Api from 'services/Api';

    export default {
        name: 'typeahead',

        props: {
            label: {
                type: String,
                required: true
            },
            required: {
                type: Boolean,
                required: true,
            },
            errorMessage: {
                type: String,
                required: true
            },
            type: {
                type: String,
                required: true
            },
            value: {
                type: Object,
            }
        },

        watch: {
            value: {
                handler: function (val) {
                    if (val === null) {
                        this.reset();
                        this.query = '';
                    }
                }
            }
        },

        mixins: [
            FormValidator
        ],

        components: {
            Control,
            ControlError,
        },

        data() {
            return {
                src: '/api/v1/contacts/search',
                items: [],
                query: (this.value || {}).name,
                current: -1,
                loading: false,
                limit: 5,
                minChars: 1,
                selectFirst: false,
                inFocus: true
            }
        },

        computed: {
            hasItems() {
                if (this.inFocus == true) {
                    return this.items.length > 0
                }
            },
            isEmpty() {
                return !this.query
            },
            isDirty() {
                return !!this.query
            },
        },

        created() {

        },

        methods: {
            update() {
                this.inFocus = true;

                if (!this.query) {
                    this.$emit('update', {id: null, name: null, type: null});
                    return this.reset();
                }

                if (this.minChars && this.query.length < this.minChars) {
                    return
                }

                this.loading = true;

                this.fetch().then((response) => {
                    if (response && this.query) {
                        let data = response.data;
                        data = this.prepareResponseData ? this.prepareResponseData(data) : data;

                        function removeDuplicates(myArr, prop) {
                            return myArr.filter((obj, pos, arr) => {
                                return arr.map(mapObj => mapObj[prop]).indexOf(obj[prop]) === pos;
                            });
                        }

                        let newData = removeDuplicates(data, 'name');

                        this.items = this.limit ? newData.slice(0, this.limit) : newData;
                        this.current = -1;
                        this.loading = false;

                        if (this.selectFirst) {
                            this.down()
                        }
                    }

                    this.$emit('update', {id: null, name: this.query, type: this.type});
                })
            },

            fetch() {
                let cancel = new Promise((resolve) => this.cancel = resolve);
                let request = Api.contacts.search({search: this.query, type: this.type});

                return Promise.race([cancel, request])
            },

            reset() {
                this.inFocus = false;
                this.items = [];
                this.loading = false;

                if (this.query !== null && this.query.length > 0) {
                    this.query = this.query.trim();
                    this.$emit('update', {id: null, name: this.query, type: this.type});
                }
            },

            setActive(index) {
                this.current = index
            },

            activeClass(index) {
                return {
                    active: this.current === index
                }
            },

            hit() {
                this.inFocus = false;
                if (this.current !== -1) {
                    this.onHit(this.items[this.current])
                }
            },

            up() {
                if (this.current > 0) {
                    this.current--
                } else if (this.current === -1) {
                    this.current = this.items.length - 1
                } else {
                    this.current = -1
                }
            },

            down() {
                this.inFocus = true;
                if (this.current < this.items.length - 1) {
                    this.current++
                } else {
                    this.current = -1
                }
            },

            onHit(item) {
                this.query = item.name;
                this.$emit('update', item);
            }
        },
    }
</script>

<style scoped>
    .Typeahead {
        position: relative;
    }

    .Typeahead__input {
        width: 100%;
        font-size: 14px;
        color: #2c3e50;
        line-height: 1.42857143;
        box-shadow: inset 0 1px 4px rgba(0, 0, 0, .4);
        -webkit-transition: border-color ease-in-out .15s, -webkit-box-shadow ease-in-out .15s;
        transition: border-color ease-in-out .15s, box-shadow ease-in-out .15s;
        font-weight: 300;
        padding: 12px 26px;
        border: none;
        border-radius: 22px;
        letter-spacing: 1px;
        box-sizing: border-box;
    }

    .Typeahead__input:focus {
        border-color: #4fc08d;
        outline: 0;
        box-shadow: inset 0 1px 1px rgba(0, 0, 0, .075), 0 0 8px #4fc08d;
    }

    .fa-times {
        cursor: pointer;
    }

    i {
        float: right;
        position: relative;
        top: 30px;
        right: 29px;
        opacity: 0.4;
    }

    ul {
        position: absolute;
        padding: 0;
        margin-top: -40px;
        min-width: calc(100% - 196px);
        background-color: #fff;
        list-style: none;
        border-radius: 4px;
        box-shadow: 0 0 10px rgba(0, 0, 0, 0.25);
        z-index: 3;
        margin-left: 160px;
    }

    li {
        padding: 10px 16px;
        border-bottom: 1px solid #ccc;
        cursor: pointer;
    }

    li:first-child {
        border-top-left-radius: 4px;
        border-top-right-radius: 4px;
    }

    li:last-child {
        border-bottom-left-radius: 4px;
        border-bottom-right-radius: 4px;
        border-bottom: 0;
    }

    .active {
        background-color: #4A89E5;
    }

    .active span {
        color: white;
    }

    .name {
        font-weight: 700;
        font-size: 18px;
    }

    .screen-name {
        font-style: italic;
    }
</style>



// WEBPACK FOOTER //
// Typeahead.vue?491fa583